import { Injectable } from '@angular/core';
import { TranslocoService } from '@ngneat/transloco';
import { Store } from '@ngxs/store';
import { distinctUntilChanged, skip } from 'rxjs/operators';

import { Ticket, TicketItem } from 'src/app/models';
import {
  SecondScreenTicket,
  SecondScreenLabels,
  SecondScreenMoney
} from 'src/app/models/hardware/external-screen.model';
import { PaymentConstant } from 'src/app/constants';
import { PAYMENT_TYPE_KEYS } from 'src/app/pipes/payment-type-label/payment-type-label.pipe';
import { AppSelectors } from 'src/app/store/app/app.selectors';
import { pickTranslatedName } from 'src/app/utils';
import { EloExternalScreen } from './elo-external-screen';

/** Locale used when none has been selected yet. */
const FALLBACK_LOCALE = 'en-US';

/**
 * The app has no facility currency field anywhere, and Angular's `| currency`
 * defaults to USD, so the second screen matches that rather than inventing a
 * currency the operator's own ticket does not know about.
 */
const DISPLAY_CURRENCY = 'USD';

type DisplayMode = 'order' | 'payment';

@Injectable({
  providedIn: 'root'
})
export class ExternalScreenProvider {
  static screenGraphics = '';

  externalScreenInstance: EloExternalScreen;
  defaultGraphicScreen = 'mealsuite-pos.png';
  isQuickService = false;
  isActive = false;

  /**
   * The last ticket pushed to the customer display, kept raw (pre-translation)
   * so a locale change can re-render it against the new language.
   */
  private lastDisplay: { mode: DisplayMode; ticket: Ticket } = null;

  constructor(
    private readonly translocoService: TranslocoService,
    private readonly store: Store
  ) {
    this.externalScreenInstance = new EloExternalScreen();
    ExternalScreenProvider.screenGraphics =
      ExternalScreenProvider.screenGraphics || this.defaultGraphicScreen;

    // The ticket streams that drive the customer display dedupe on ticket
    // shape, so a language switch emits nothing and the customer keeps reading
    // the old language. Re-push from here instead: one subscription covers
    // every call site and leaves their dedupe guards alone.
    this.translocoService.langChanges$
      .pipe(distinctUntilChanged(), skip(1))
      .subscribe(() => this.repushLastDisplay());
  }

  startWelcomeScreen(): void {
    this.externalScreenInstance.startWelcomeScreen();
    this.isQuickService = true;
  }

  initializeOrder(): void {
    this.externalScreenInstance.initializeOrder(null);
  }

  displayOrderDetails(orderDetails: Ticket): void {
    if (this.isQuickService) {
      this.lastDisplay = { mode: 'order', ticket: orderDetails };
      this.externalScreenInstance.displayOrderDetails(
        this.withDisplayLabels(orderDetails)
      );
    }
  }

  displayPaymentDetails(paymentDetails: Ticket) {
    if (this.isQuickService) {
      this.lastDisplay = { mode: 'payment', ticket: paymentDetails };
      this.externalScreenInstance.displayPaymentDetails(
        this.withDisplayLabels(paymentDetails)
      );
    }
  }

  private repushLastDisplay(): void {
    if (!this.isQuickService || !this.lastDisplay) {
      return;
    }

    const { mode, ticket } = this.lastDisplay;
    if (mode === 'order') {
      this.displayOrderDetails(ticket);
    } else {
      this.displayPaymentDetails(ticket);
    }
  }

  private withDisplayLabels(ticket: Ticket): SecondScreenTicket {
    // Read per call, not cached: the operator can switch language at any time.
    const lang =
      this.store.selectSnapshot(AppSelectors.getSelectedLocaleCode) ??
      FALLBACK_LOCALE;

    return {
      ...ticket,
      ticket_items: this.translateItems(ticket?.ticket_items, lang),
      transactions: ticket?.transactions?.map((transaction) => {
        const key =
          PAYMENT_TYPE_KEYS[
            PaymentConstant.PAYMENT_REQUEST_TYPE_MAP[transaction.type]
          ];
        // Leave typeLabel unset for an unmapped type so the receiver falls
        // back to its own lookup and then '--'. Defaulting to a real label
        // here would show the customer a payment type they did not use.
        return key
          ? {
              ...transaction,
              typeLabel: this.translocoService.translate(key)
            }
          : transaction;
      }),
      labels: this.buildLabels(),
      money: this.buildMoney(lang)
    };
  }

  private buildLabels(): SecondScreenLabels {
    return {
      assignedTo: this.translocoService.translate('secondScreen.assignedTo'),
      subtotal: this.translocoService.translate('secondScreen.subtotal'),
      discounts: this.translocoService.translate('secondScreen.discounts'),
      taxTotal: this.translocoService.translate('pos.taxTotal'),
      remainingBalance: this.translocoService.translate(
        'secondScreen.remainingBalance'
      ),
      changeDue: this.translocoService.translate('payment.changeDue'),
      orderSummary: this.translocoService.translate(
        'secondScreen.orderSummary'
      ),
      // The count comes from the receiver's own overflow calculation, so ship
      // the translated sentence with a literal token for it to substitute.
      additionalItems: this.translocoService.translate(
        'secondScreen.additionalItems',
        { count: '{count}' }
      ),
      coveredByMealPlan: this.translocoService.translate(
        'quickService.coverByMealPlan'
      ),
      total: this.translocoService.translate('pos.total'),
      thankYou: this.translocoService.translate('secondScreen.thankYouMessage')
    };
  }

  private buildMoney(lang: string): SecondScreenMoney {
    return { locale: lang, currency: DISPLAY_CURRENCY };
  }

  /**
   * Resolves item and nested modifier names to the active locale so the
   * customer display reads the same as the operator's ticket panel, which
   * renders through the same `pickTranslatedName`.
   *
   * `name_translations` is dropped on the way out: the receiver never reads it,
   * and this payload is posted on every ticket mutation.
   */
  private translateItems(
    items: TicketItem[] | undefined,
    lang: string
  ): TicketItem[] {
    return (items ?? []).map(({ name_translations, ...item }) => ({
      ...item,
      name: pickTranslatedName({ name: item.name, name_translations }, lang),
      ticket_items: this.translateItems(item.ticket_items, lang)
    }));
  }

  displayHome() {
    // A finished transaction must not come back on a later language switch.
    this.lastDisplay = null;
    this.externalScreenInstance.displayHome();
  }

  showMessage(message: any) {
    this.externalScreenInstance.showMessage(message);
  }

  private displayGraphics() {
    this.externalScreenInstance.updateGraphics(
      ExternalScreenProvider.screenGraphics
    );
  }

  updateGraphics(graphicsUrl: string = null) {
    ExternalScreenProvider.screenGraphics = graphicsUrl
      ? graphicsUrl
      : this.defaultGraphicScreen;
    this.displayGraphics();
  }
  testData(message: any) {
    console.log('External Screen -> testData', message);
  }

  turnOnBacklight() {}

  turnOffBacklight() {}
}
